<template>
  <div class="time-panel">
    <div class="tp-header"><i class="fas fa-stopwatch"></i><h3>Zeiterfassung — {{ stageLabel }}</h3></div>

    <div v-if="!trackable" class="tp-note">
      Zeiterfassung ab der Vorstufe (Status „Auftrag erteilt" wird noch geprüft).
    </div>

    <template v-else>
      <!-- Live-Netto vs. Kalkulation -->
      <div class="tp-time" :class="{ over: overtime }">
        <span class="tp-net">{{ fmtSeconds(liveNetSeconds) }}</span>
        <span class="tp-sep">/</span>
        <span v-if="hasCalc" class="tp-calc">{{ fmtHM(calcH) }}</span>
        <span v-else class="tp-nocalc" title="Für diese Etappe ist keine Zeit kalkuliert">keine Kalk-Zeit</span>
        <span v-if="overtime" class="tp-over">Überzug!</span>
        <span class="tp-state">· {{ stateLabel }}</span>
      </div>

      <!-- B6 — harte Maschinen-Sperre. Als bleibender Hinweis statt nur als
           Toast: der Bearbeiter muss wissen, WELCHER Auftrag ihn blockiert. -->
      <div v-if="blocker" class="tp-blocked">
        <i class="fas fa-lock"></i>
        <span>
          Maschine noch belegt durch
          <router-link :to="`/auftraege/${blocker.id}`" class="tp-blocked-link">{{ blocker.order_number }}</router-link>
          — dieser Auftrag muss zuerst auf „Fertig" gesetzt werden.
        </span>
      </div>

      <!-- Aktionsleiste je Zustand -->
      <div class="tp-actions" v-if="canEdit && !cancelled">
        <button v-if="notStarted" class="tp-btn start" :disabled="busy" @click="stamp('start')">
          <i class="fas fa-play"></i> Begonnen
        </button>
        <template v-else-if="running">
          <button class="tp-btn pause" :disabled="busy" @click="stamp('pause_start')"><i class="fas fa-pause"></i> Pause</button>
          <button class="tp-btn done" :disabled="busy" @click="stamp('complete')"><i class="fas fa-check"></i> Fertig</button>
        </template>
        <template v-else-if="paused">
          <button class="tp-btn resume" :disabled="busy" @click="stamp('pause_end')"><i class="fas fa-play"></i> Weiter</button>
          <button class="tp-btn done" :disabled="busy" @click="stamp('complete')"><i class="fas fa-check"></i> Fertig</button>
        </template>
        <span v-else-if="completed" class="tp-finished"><i class="fas fa-flag-checkered"></i> Etappe abgeschlossen</span>
      </div>
      <div v-else-if="cancelled" class="tp-note">Storniert — keine Zeiterfassung.</div>
      <div v-else class="tp-note">Nur der zugewiesene Bearbeiter (oder Admin) kann stempeln.</div>

      <!-- Protokoll -->
      <ul class="tp-log">
        <li v-for="e in entries" :key="e.id">
          <b>{{ kindLabel(e.kind) }}</b>
          <span class="tp-log-meta">{{ formatAt(e.at) }} · {{ e.employee_kuerzel || '—' }} · {{ e.stage }}</span>
        </li>
        <li v-if="!entries.length" class="tp-empty">Noch keine Zeiterfassung.</li>
      </ul>
    </template>
  </div>
</template>

<script>
import api from '@/api'
import { fmtHM, fmtSeconds, calcHoursForStage, isOvertime } from '@/utils/timeCalc'
import { machineCategory } from '@/utils/machineNames'

const TRACKABLE = ['prepress_complete', 'plates_complete', 'printing_complete', 'processing_complete', 'shipping_complete']
const KIND_LABELS = { start: 'Begonnen', pause_start: 'Pause', pause_end: 'Weiter', complete: 'Fertig' }
const STAGE_LABELS = {
  prepress_complete: 'Vorstufe', plates_complete: 'CtP / Platten', printing_complete: 'Druck',
  processing_complete: 'Weiterverarbeitung', shipping_complete: 'Versand'
}

export default {
  name: 'OrderTimePanel',
  props: {
    order: { type: Object, required: true },
    canEdit: { type: Boolean, default: false }
  },
  data() {
    return { entries: [], timeState: {}, fetchedAt: 0, tick: Date.now(), busy: false, blocker: null, _timer: null }
  },
  computed: {
    trackable() { return TRACKABLE.includes(this.order.status) },
    cancelled() { return this.order.status === 'cancelled' },
    stageLabel() { return STAGE_LABELS[this.order.status] || this.order.status },
    running() { return !!this.timeState.running },
    paused() { return !!this.timeState.paused },
    completed() { return !!this.timeState.completed },
    notStarted() { return !this.running && !this.paused && !this.completed },
    // Kalk-Zeit DIESER Etappe (Planfenster ihrer Maschine), nicht des ganzen
    // Auftrags. null → kein Vergleich, kein Überzug (Vorstufe, CtP, Versand).
    calcH() { return calcHoursForStage(this.order, this.order.status, machineCategory) },
    hasCalc() { return this.calcH != null && this.calcH > 0 },
    liveNetSeconds() {
      const ts = this.timeState
      if (!ts || ts.net_seconds == null) return 0
      let n = ts.net_seconds
      if (ts.running && this.fetchedAt) n += Math.max(0, (this.tick - this.fetchedAt) / 1000)
      return n
    },
    // Bewusst direkt aus calcH abgeleitet statt aus hasCalc: die Computeds
    // werden in den Tests einzeln mit .call(ctx) aufgerufen, und eine Kette
    // von Computeds macht daraus stillschweigend `undefined`.
    overtime() {
      const calc = this.calcH
      return calc != null && calc > 0 && isOvertime(this.liveNetSeconds / 3600, calc)
    },
    stateLabel() {
      if (this.completed) return 'fertig'
      if (this.paused) return 'pausiert'
      if (this.running) return 'läuft'
      return 'nicht begonnen'
    }
  },
  watch: {
    'order.id'() { this.load() },
    'order.status'() { this.load() } // nach „Fertig" rückt der Status → neue Etappe laden
  },
  mounted() {
    this.load()
    this._timer = setInterval(() => { this.tick = Date.now() }, 30000)
  },
  beforeUnmount() {
    if (this._timer) clearInterval(this._timer)
  },
  methods: {
    fmtHM, fmtSeconds,
    kindLabel(k) { return KIND_LABELS[k] || k },
    formatAt(at) { return at ? new Date(at).toLocaleString('de-DE') : '—' },
    async load() {
      if (!this.order.id || !this.trackable) return
      try {
        const res = await api.get(`orders/${this.order.id}/time_entries`)
        this.entries = res.data || []
        this.timeState = res.time_state || {}
        this.fetchedAt = Date.now()
        this.tick = Date.now()
      } catch (e) {
        console.error('Zeiterfassung laden fehlgeschlagen:', e)
      }
    },
    async stamp(kind) {
      this.busy = true
      this.blocker = null
      try {
        const res = await api.post(`orders/${this.order.id}/time_entries`, { kind })
        if (res.data) this.entries.unshift(res.data)
        this.timeState = res.time_state || {}
        this.fetchedAt = Date.now()
        this.tick = Date.now()
        this.$toast?.success(`${this.kindLabel(kind)} erfasst`)
        this.$emit('updated') // Elternteil lädt den Auftrag neu (Status kann sich geändert haben)
      } catch (e) {
        const data = e?.response?.data || {}
        if (data.blocking_order_id) {
          this.blocker = { id: data.blocking_order_id, order_number: data.blocking_order_number }
        }
        this.$toast?.error(data.error || 'Aktion nicht möglich')
      } finally {
        this.busy = false
      }
    }
  }
}
</script>

<style scoped>
.time-panel { background: #fff; border: 1px solid #e5e7eb; border-radius: 12px; padding: 1rem 1.25rem; }
.tp-header { display: flex; align-items: center; gap: 0.6rem; color: #374151; margin-bottom: 0.75rem; }
.tp-header h3 { margin: 0; font-size: 1rem; }
.tp-note { color: #6b7280; font-size: 0.85rem; padding: 0.4rem 0; }

/* B6 — Maschinen-Sperre */
.tp-blocked {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  background: #fef2f2;
  border: 1px solid #fca5a5;
  border-radius: 8px;
  color: #b91c1c;
  font-size: 0.85rem;
  font-weight: 600;
  padding: 0.5rem 0.7rem;
  margin-bottom: 0.6rem;
}
.tp-blocked-link { color: #b91c1c; text-decoration: underline; }
.tp-time { font-size: 1.15rem; font-weight: 700; color: #111827; display: flex; align-items: baseline; gap: 0.4rem; margin-bottom: 0.75rem; }
.tp-time.over { color: #b91c1c; }
.tp-calc { color: #6b7280; font-weight: 600; }
.tp-over { background: #b91c1c; color: #fff; font-size: 0.7rem; padding: 1px 6px; border-radius: 4px; font-weight: 700; }
.tp-state { color: #9ca3af; font-size: 0.8rem; font-weight: 500; }
.tp-actions { display: flex; gap: 0.5rem; flex-wrap: wrap; margin-bottom: 0.75rem; }
.tp-btn { display: inline-flex; align-items: center; gap: 0.4rem; padding: 0.5rem 0.9rem; border: none; border-radius: 8px; font-weight: 600; font-size: 0.88rem; cursor: pointer; color: #fff; }
.tp-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.tp-btn.start, .tp-btn.resume { background: #16a34a; }
.tp-btn.pause { background: #d97706; }
.tp-btn.done { background: #2563eb; }
.tp-finished { color: #16a34a; font-weight: 600; display: inline-flex; align-items: center; gap: 0.4rem; }
.tp-log { list-style: none; margin: 0; padding: 0; max-height: 200px; overflow-y: auto; border-top: 1px solid #f3f4f6; }
.tp-log li { display: flex; justify-content: space-between; gap: 1rem; padding: 0.35rem 0; border-bottom: 1px solid #f3f4f6; font-size: 0.82rem; }
.tp-log-meta { color: #6b7280; }
.tp-empty { color: #9ca3af; justify-content: flex-start !important; }
.tp-nocalc { color: #8a8f98; font-style: italic; font-size: .85em; }

</style>
